/**
 * 角色组合种子类型定义
 *
 * 定义"无 LLM 路径"下实例化一个角色所需的最小输入。
 *
 * 实例化流程（参见 CharacterInstantiationService）：
 *   raceId + buildTemplateId? + tags? + rank? + rngSeed?
 *   → race baseline + build template bonus + tags modifier + 骰子
 *   → 完整 Character
 *
 * 同一个 seed 配合相同 rngSeed 应当复现出完全一致的角色。
 * 这是组合路径与 LLM 路径的关键差异 —— 组合路径是确定性的、可缓存的。
 */
import { z } from 'zod';
import {
  normalizeRaceId,
  normalizeOptionalBuildTemplateId,
  normalizeOptionalIdentityTemplateId,
} from '@/IsekaiRPG/modules/character/characterSemantic';
import { RankSchema } from '@/IsekaiRPG/shared/types';

// ============================================
// 角色组合种子
// ============================================

/**
 * 角色组合种子
 */
export const CharacterCompositionSeedSchema = z.object({
  /** 种族 ID（必填，决定基线属性与可习得能力） */
  raceId: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string().min(1)),
  /** 社会/建筑互动模板 ID（可选，缺省表示该角色无特殊身份模板） */
  identityTemplateId: z
    .preprocess(
      value => normalizeOptionalIdentityTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  /** 内部构筑模板 ID（可选，缺省表示该角色无额外构筑模板） */
  buildTemplateId: z
    .preprocess(
      value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  /** 角色标签列表；命中词库的标签会参与机制，未命中标签保留为纯语义 */
  tags: z.array(z.string()).default([]),
  /**
   * 大致强度评级
   *
   * 决定属性骰的范围与抽取技能时的阶级中心点：
   * - F-D：杂兵级
   * - C-B：精英级
   * - A-S：首领级
   *
   * 这只是骰子范围参数，不会作为 schema 字段写入 Character。
   * 一个 rank=C 的哥布林与一个 rank=C 的龙仍然有数量级差距，
   * 因为种族基线本身就已经体现了强度差。
   */
  rank: RankSchema.optional(),
  /**
   * 随机种子（可选）
   *
   * 同 seed + 同 rngSeed → 完全一致的输出，用于复现与缓存。
   * 缺省时使用一次性随机数。
   */
  rngSeed: z.string().optional(),
  /**
   * 名字覆盖（可选）
   *
   * 调用方明确知道"这是某某某"时使用，跳过命名规则。
   */
  nameOverride: z.string().optional(),
});
export type CharacterCompositionSeed = z.infer<typeof CharacterCompositionSeedSchema>;
